/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Pure builders for the crystal MD engine — no React, no rendering — so
 * the Web Worker and the on-screen viewer can both construct the engine
 * off one source of truth. The build is deliberately split in two:
 *
 *   buildCrystalScaffold     — STRUCTURAL: expand the supercell into
 *                              atoms, build the radial-bond graph and
 *                              molecule labels, the periodic cell. No
 *                              physics, no force field, no container —
 *                              safe to memoize against (crystal,
 *                              supercell) alone.
 *   configureCrystalDynamics — PHYSICS: wrap a scaffold in a
 *                              CrystalDynamics and hand it to the
 *                              selected ForceField (see
 *                              crystalForceFields.ts) for all
 *                              physics-specific setup.
 *
 * buildCrystalDynamics runs both stages in one call — the Web Worker
 * uses it, since it has no separate rendering scaffold to keep. The
 * viewer keeps the scaffold (it also needs the atoms to render) and
 * calls configureCrystalDynamics on its own, so that resizing the
 * container or changing the time step does not rebuild the structure.
 */
import { Vector3 } from 'three';
import {
  CrystalDynamicsSnapshot,
  CrystalDynamicsSnapshotSource,
  CrystalGuest,
  CrystalState,
  MolecularContainer,
  Dopant,
} from '../../types.ts';
import { expandSupercell, expandSupercellCompact, latticeVectors } from '../../crystal/Crystal.ts';
import { applyDefects, applyDopants } from '../../semiconductor/dopantPlacement';
import { computeBonds } from '../../crystal/elementVisuals.ts';
import { Atom } from '../Atom.ts';
import { RadialBond } from '../RadialBond.ts';
import { enumerateBonds, labelMolecules } from './bondGraph.ts';
import { detectMolecularSpecies } from './forceFields.ts';
import { resolveForceField } from './crystalForceFields.ts';
import { ionicPackFor } from './buckinghamParameters.ts';
import { metallicPackFor } from './morseParameters.ts';
import { eamPackFor } from './eamParameters.ts';
import { swPackFor } from './swParameters.ts';
import { tersoffPackFor } from './tersoffParameters.ts';
import { CrystalDynamics } from './CrystalDynamics.ts';
import { ConstraintSet } from './RigidConstraints.ts';
import { PeriodicBox } from './PeriodicBox.ts';
import { buildGuestAtoms } from './crystalGuests.ts';
import { Restraint } from '../Restraint.ts';

export interface SupercellSize {
  nx: number;
  ny: number;
  nz: number;
}

/** Structural result of expanding a crystal over a supercell. */
export interface CrystalScaffold {
  /** Expanded atoms, origin-centered, with the MD scratch vectors filled. */
  atoms: Atom[];
  /** Intramolecular bonds — empty for atomic (metallic/ionic) crystals. */
  radialBonds: RadialBond[];
  /** atomIndex → molecule-cluster id (BFS over the radial-bond graph). */
  moleculeOfAtom: number[];
  /** The supercell as a periodic cell, ready for setPeriodicBox(). */
  periodicBox: PeriodicBox;
  /** Geometric center of the supercell (atoms were shifted by -center). */
  center: Vector3;
}

/**
 * Stage 1 — STRUCTURAL. Expand `crystal` over `supercell` into a set of
 * origin-centered atoms plus the bond graph and periodic cell, then
 * append any guest molecules. Pure: no force field, no CrystalDynamics,
 * no container — the result depends only on (crystal, supercell,
 * guests), so the viewer can memoize against it.
 */
export const buildCrystalScaffold = (
  crystal: CrystalState,
  supercell: SupercellSize,
  guests: CrystalGuest[] = [],
): CrystalScaffold => {
  const { nx, ny, nz } = supercell;

  // Structural classification — it decides the supercell expansion. A
  // molecular/covalent crystal (anything that gets explicit bonds) MUST
  // use the compact, non-duplicating expansion: the visualization
  // `expandSupercell` duplicates atoms on the upper-boundary faces,
  // which splits molecules sitting on a cell face and corrupts the MD
  // topology. Atomic crystals (metallic/ionic) have no molecules to
  // split, so they keep the textbook conventional-cell look.
  const species = detectMolecularSpecies(crystal.basis);
  const isMetallic = crystal.metallic ?? false;
  // Ionic for routing purposes: any crystal where a Buckingham pack
  // will fire OR the preset explicitly carries formal charges. The
  // pack-driven path covers cristobalite (BKS SiO₂) — the preset
  // doesn't set elementCharges (SiO₂ is structurally covalent and
  // grouped as such in the UI), but BKS supplies partial charges
  // through the pack and the crystal needs the ionic FF's bond-less,
  // Ewald-equipped setup. Without this, the molecular branch would
  // auto-detect Si-O bonds (NN = 1.55 Å is well inside the bonding
  // cutoff), connect every atom into one giant "molecule" via the
  // bond graph, and the kernel's same-molecule skip would then
  // exclude every Coulomb pair — sending cohesion strongly positive.
  const isIonic = species === null && (!!crystal.elementCharges || ionicPackFor(crystal) !== null);
  // Covalent: there's a Stillinger-Weber or Tersoff pack for this
  // composition. Covalent crystals are neither metallic nor ionic-
  // flagged in the preset table — they are tetrahedral or sp² networks
  // of neutral atoms — so a covalent-FF pack registration is what flags
  // them. Either pack is sufficient (Si has both; C has only Tersoff).
  const isCovalent = !isMetallic && !isIonic && (swPackFor(crystal) !== null || tersoffPackFor(crystal) !== null);
  // Noble-gas (inert) crystal: monatomic basis of a single noble gas
  // element. Pure van der Waals cohesion, no bonds. Treated as
  // molecular (for compact PBC expansion) but with NO bond detection.
  const NOBLE = new Set(['He', 'Ne', 'Ar', 'Kr', 'Xe', 'Rn']);
  const elementSet = new Set(crystal.basis.map((b) => b.element));
  const isNobleGas = elementSet.size === 1 && NOBLE.has([...elementSet][0]);
  // "molecular" here means "anything that gets explicit radial bonds"
  // — water, organic crystals. Covalent crystals on SW do NOT get
  // radial bonds: the SW V₂ IS the bond term, and a separate harmonic
  // bond would double-count it (and the kernel's bonded-pair skip
  // would then exclude every NN from V₂, breaking the potential).
  // Noble-gas crystals are also bond-less (vdW only) so they take the
  // molecular branch for PBC expansion but skip the bond pass below.
  const molecular = !isMetallic && !isIonic && !isCovalent;
  // Compact (non-duplicating) expansion is required when the resolved
  // force field runs under periodic boundaries — water always does, an
  // ionic crystal does iff it has a Buckingham pack registered (ionic
  // FF + Ewald), a metallic crystal does iff it has a Morse pack
  // registered (metallic FF + PBC), and a covalent crystal does iff it
  // has an SW pack registered. Without a pack each falls through to
  // the generic FF in finite mode, which needs the duplicated expansion:
  // bare-1/r Coulomb (ionic) or LJ (metallic) on a compact cluster sees
  // unbalanced surface attraction and the cluster collapses, sending
  // atoms off-screen. The duplicated boundary layer symmetrizes those
  // surface forces. (A covalent crystal without an SW pack collapses
  // even more dramatically on LJ — diamond is not LJ-stable.)
  const usesPeriodicBox =
    molecular ||
    (isIonic && ionicPackFor(crystal) !== null) ||
    (isMetallic && (metallicPackFor(crystal) !== null || eamPackFor(crystal) !== null)) ||
    isCovalent;
  let atoms = usesPeriodicBox ? expandSupercellCompact(crystal, nx, ny, nz) : expandSupercell(crystal, nx, ny, nz);

  // Apply Semiconductor Design dopants (if any) to the expanded supercell.
  // Substitutes specific host atoms with the dopant species using a
  // deterministic PRNG seeded from (name, nx, ny, nz), so the same crystal
  // at the same supercell size always shows the same dopant placements.
  // Pristine crystals (no dopants[]) are a no-op.
  //
  // An ALLOY is applied through the same machinery, as an isovalent
  // substitution at x·100 at.%. Al(0.3)Ga(0.7)As really is 30% of the Ga
  // sites carrying Al, so the supercell should show that — and it goes
  // FIRST, in the same call, so the two passes share one RNG stream and
  // one `claimed` set. Doping afterwards then draws from the sites the
  // alloy left alone, which is both the correct physics and the reason
  // this cannot be two separate applyDopants calls (they would seed
  // identically and fight over the same sites).
  const substitutions: Dopant[] = [];
  if (crystal.alloy && crystal.alloy.fraction > 0) {
    const first = crystal.alloy;
    substitutions.push({
      hostElement: first.hostElement,
      dopantElement: first.substituteElement,
      concentrationAtPct: first.fraction * 100,
      type: 'i',
    });
    // A quaternary's second substitution. Its fraction is of the WHOLE
    // sublattice, but applyDopants counts against the host sites still left:
    // on the same sublattice (Al(x)Ga(y)In(1-x-y)P) the first pass has
    // already taken x of them, so y is rescaled to y/(1-x); on the other
    // sublattice (InGaAsP) nothing was taken and y stands.
    const second = first.second;
    if (second && second.fraction > 0) {
      const left = second.hostElement === first.hostElement ? 1 - first.fraction : 1;
      if (left > 1e-9) {
        substitutions.push({
          hostElement: second.hostElement,
          dopantElement: second.substituteElement,
          concentrationAtPct: Math.min(100, (second.fraction / left) * 100),
          type: 'i',
        });
      }
    }
  }
  if (crystal.dopants && crystal.dopants.length > 0) substitutions.push(...crystal.dopants);
  if (substitutions.length > 0) {
    applyDopants(atoms, substitutions, crystal.name ?? '', nx, ny, nz);
  }
  // Apply point defects (vacancies + interstitials). Vacancies remove host
  // atoms, interstitials insert new ones at random fractional positions.
  // Returns a fresh array because vacancy filtering can't be done in-place
  // on a const reference — reassigning `atoms` is fine (it's local to
  // this function). Defects run AFTER dopants so vacancies can remove
  // already-doped atoms too — the user's intent is "remove some of this
  // species" regardless of provenance.
  if (crystal.defects && crystal.defects.length > 0) {
    atoms = applyDefects(atoms, crystal.defects, crystal.name ?? '', nx, ny, nz, crystal.lattice);
  }

  const [a, b, c] = latticeVectors(crystal.lattice);
  const center = new Vector3(
    (nx * a.x + ny * b.x + nz * c.x) / 2,
    (nx * a.y + ny * b.y + nz * c.y) / 2,
    (nx * a.z + ny * b.z + nz * c.z) / 2,
  );
  const periodicBox = new PeriodicBox();
  // A vacuum gap lengthens the box along c WITHOUT moving any atom, so the
  // supercell becomes a slab: across its top and bottom faces it now sees
  // `vacuumAng` of empty space instead of its own periodic image. Atoms stay
  // centered on the supercell (below), so the gap splits evenly above and
  // below the slab. a and b stay fully periodic. Ewald is fine with this for a
  // charge-neutral, non-polar slab (rock-salt (001)); the only cost is more
  // k-vectors along the long axis.
  const cBox =
    crystal.vacuumAng && crystal.vacuumAng > 0
      ? c.clone().multiplyScalar(nz).add(c.clone().normalize().multiplyScalar(crystal.vacuumAng))
      : c.clone().multiplyScalar(nz);
  periodicBox.setLattice(a.clone().multiplyScalar(nx), b.clone().multiplyScalar(ny), cBox);

  // Center atom positions around the origin and fill the velocity-Verlet
  // scratch vectors — expandSupercell builds plain Atoms without them.
  for (const atom of atoms) {
    atom.position.sub(center);
    atom.displacement = new Vector3();
    atom.acceleration = new Vector3();
    atom.initialPosition = atom.position.clone();
    atom.initialVelocity = new Vector3();
  }

  const radialBonds: RadialBond[] = [];
  if (molecular && !isNobleGas) {
    // Skip computeBonds for noble-gas crystals: they're vdW-bound, no
    // covalent or harmonic bonds. The distance cutoff (1.15·sum of
    // bonding radii) would otherwise add spurious "bonds" between
    // FCC nearest neighbors (Ar-Ar at 3.76 Å is roughly within
    // 1.15·(1.88+1.88) = 4.32 Å), which the kernel's bonded-pair
    // skip would then remove from the LJ pair sum — breaking cohesion.
    for (const bp of computeBonds(atoms, undefined)) {
      radialBonds.push(new RadialBond(atoms[bp.a], atoms[bp.b]));
    }
  }

  // Append guest molecules — non-periodic insertions (a clathrate guest,
  // a dopant, a solute) placed in the chamber's origin-centered frame,
  // the same frame the host atoms now live in. Each guest's bonds come
  // from the library's explicit topology, NOT distance-guessing, so a
  // guest sitting close to the host cannot pick up a spurious
  // cross-bond — computeBonds above ran on the host atoms alone.
  for (const guest of guests) {
    const base = atoms.length;
    const { atoms: guestAtoms, bonds: guestBonds } = buildGuestAtoms(guest, base);
    for (const ga of guestAtoms) {
      ga.displacement = new Vector3();
      ga.acceleration = new Vector3();
      ga.initialPosition = ga.position.clone();
      ga.initialVelocity = new Vector3();
      atoms.push(ga);
    }
    for (const [i, j] of guestBonds) {
      radialBonds.push(new RadialBond(atoms[base + i], atoms[base + j]));
    }
  }

  // Map atomIndex → molecule-cluster id, then stamp it onto each atom's
  // moleculeName — the Coulomb kernel uses it as the intramolecular
  // exclusion mask. Isolated atoms each get their own one-atom cluster.
  const moleculeOfAtom = labelMolecules(atoms, radialBonds);
  for (let i = 0; i < atoms.length; i++) atoms[i].moleculeName = 'c' + moleculeOfAtom[i];

  return { atoms, radialBonds, moleculeOfAtom, periodicBox, center };
};

/**
 * Give every multi-atom guest molecule in `dyn` its rigid-body
 * constraints — all-pairwise distance constraints from the built
 * (placed) geometry. SHAKE handles the redundancy for guests with more
 * than three atoms; monatomic guests (noble gases, united-atom methane)
 * need none.
 *
 * A guest joins a rigid host (water — `dyn` already carries constraints)
 * as a member of the same constraint set; for an atomic host (no bonds)
 * a fresh set is created. But if the host is a FLEXIBLE molecular
 * crystal — host-host radial bonds, no constraints — adding any
 * constraint would flip the engine's global rigid flag and silently
 * disable the host's harmonic bonds; there, guests are left flexible
 * and their own radial bonds give them harmonic intramolecular forces.
 */
const addGuestConstraints = (dyn: CrystalDynamics, radialBonds: RadialBond[]): void => {
  const groups = new Map<string, number[]>();
  for (let i = 0; i < dyn.atoms.length; i++) {
    const gid = dyn.atoms[i].guestId;
    if (gid === undefined) continue;
    const g = groups.get(gid);
    if (g) g.push(i);
    else groups.set(gid, [i]);
  }
  if (groups.size === 0) return;
  const hostRigid = dyn.constraints !== undefined && dyn.constraints.length > 0;
  const hostFlexible =
    !hostRigid && radialBonds.some((rb) => rb.atom1.guestId === undefined && rb.atom2.guestId === undefined);
  if (hostFlexible) return;
  let cs = dyn.constraints;
  for (const idx of groups.values()) {
    if (idx.length < 2) continue;
    for (let a = 0; a < idx.length - 1; a++) {
      for (let b = a + 1; b < idx.length; b++) {
        if (!cs) {
          cs = new ConstraintSet();
          dyn.constraints = cs;
        }
        const d = dyn.atoms[idx[a]].position.distanceTo(dyn.atoms[idx[b]].position);
        cs.add(idx[a], idx[b], d);
      }
    }
  }
};

/**
 * Stage 2 — PHYSICS. Wrap a scaffold in a CrystalDynamics and hand it to
 * the crystal's force field (CrystalState.forceFieldId, or the auto-
 * default for its composition — see crystalForceFields.ts) for all
 * physics-specific setup: per-atom σ/ε/charge, virtual sites, rigid vs
 * flexible bonding, periodic boundaries, time-step cap, minimization.
 */
export const configureCrystalDynamics = (
  scaffold: CrystalScaffold,
  crystal: CrystalState,
  container: MolecularContainer,
  timeStep: number,
  // True when a saved snapshot will overwrite the coordinates right after —
  // see resumeBuild below; the force field's relaxation is then skipped.
  skipMinimize = false,
): CrystalDynamics => {
  // Angular/torsional terms are derived from the radial-bond graph;
  // they are inert for rigid water but stiffen flexible crystals.
  const { angular, torsional } = enumerateBonds(scaffold.atoms, scaffold.radialBonds);
  const dyn = new CrystalDynamics(scaffold.atoms, container, scaffold.radialBonds, angular, torsional);
  dyn.countMovables();
  dyn.skipMinimize = skipMinimize;
  resolveForceField(crystal).configure({
    crystal,
    atoms: scaffold.atoms,
    radialBonds: scaffold.radialBonds,
    dynamics: dyn,
    periodicBox: scaffold.periodicBox,
    requestedTimeStep: timeStep,
  });
  dyn.skipMinimize = false;
  // Guest molecules are physics the host force field does not own — give
  // each multi-atom guest its rigid-body constraints once the host is set.
  addGuestConstraints(dyn, scaffold.radialBonds);
  dyn.updateKineticEnergy();
  return dyn;
};

/**
 * Construct a fully-initialized CrystalDynamics for `crystal` expanded
 * over `supercell`, inside `container` — the scaffold and force-field
 * stages in one call. Used by the Web Worker, which has no separate
 * rendering scaffold to keep around.
 */
export const buildCrystalDynamics = (
  crystal: CrystalState,
  supercell: SupercellSize,
  container: MolecularContainer,
  timeStep: number,
  guests: CrystalGuest[] = [],
): CrystalDynamics =>
  configureCrystalDynamics(buildCrystalScaffold(crystal, supercell, guests), crystal, container, timeStep);

/**
 * Snapshot the live coordinates + velocities of an engine's atoms for
 * persistence (see CrystalDynamicsSnapshot). Returns null when there is
 * nothing worth saving: an engine that has not stepped yet (indexOfStep 0)
 * AND carries no manual lab edits (dyn.edited) still sits on the built/relaxed
 * lattice with zero velocity, which the load path rebuilds deterministically —
 * no need to store it. Only the engine's own `atoms` are captured, so
 * render-only ghost / cosmetic image atoms (which never enter `atoms`) are
 * excluded by construction.
 *
 * When the user has manually edited the live atoms (cluster translate / rotate
 * / restrain / cut — `dyn.edited` is set), the snapshot is taken even at step 0
 * and additionally records per-atom restraint strength + fixed/removed flags so
 * those edits survive reload (translate/rotate live in `positions`). Each
 * optional array is included only if at least one atom carries a non-default
 * value, keeping pristine snapshots compact.
 */
export const captureCrystalDynamicsSnapshot = (
  dyn: CrystalDynamics,
  source?: CrystalDynamicsSnapshotSource | null,
): CrystalDynamicsSnapshot | null => {
  if (dyn.atoms.length === 0) return null;
  if (dyn.indexOfStep <= 0 && !dyn.edited) return null;
  const n = dyn.atoms.length;
  const positions = new Array<number>(n * 3);
  const velocities = new Array<number>(n * 3);
  const restraints = new Array<number>(n);
  const fixed = new Array<boolean>(n);
  const removed = new Array<boolean>(n);
  let anyRestraint = false;
  let anyFixed = false;
  let anyRemoved = false;
  for (let i = 0; i < n; i++) {
    const at = dyn.atoms[i];
    positions[i * 3] = at.position.x;
    positions[i * 3 + 1] = at.position.y;
    positions[i * 3 + 2] = at.position.z;
    velocities[i * 3] = at.velocity.x;
    velocities[i * 3 + 1] = at.velocity.y;
    velocities[i * 3 + 2] = at.velocity.z;
    const r = at.restraint?.strength ?? 0;
    restraints[i] = r;
    if (r > 0) anyRestraint = true;
    fixed[i] = !!at.fixed;
    if (at.fixed) anyFixed = true;
    removed[i] = !!at.removed;
    if (at.removed) anyRemoved = true;
  }
  const snapshot: CrystalDynamicsSnapshot = { positions, velocities, indexOfStep: dyn.indexOfStep };
  if (anyRestraint) snapshot.restraints = restraints;
  if (anyFixed) snapshot.fixed = fixed;
  if (anyRemoved) snapshot.removed = removed;
  if (source) snapshot.source = { ...source };
  return snapshot;
};

/**
 * The live frame as it will be once `guest` is added — for the rebuild that
 * adding a guest triggers to resume. Guest atoms are built after the host and
 * earlier guests, so the new guest's atoms go at the END, at rest, where its
 * transform puts them. Without this the rebuild finds a frame one guest short
 * (wrong atom count), refuses it and restarts from the lattice: cut molecules
 * come back and the run is lost. Taken even before the first step (the lab
 * may hold unsaved cuts). Null when there is no engine state to keep.
 */
export const crystalFrameAddingGuest = (
  dyn: CrystalDynamics,
  source: CrystalDynamicsSnapshotSource | null,
  guest: CrystalGuest,
): CrystalDynamicsSnapshot | null => {
  dyn.edited = true;
  const frame = captureCrystalDynamicsSnapshot(dyn, source);
  if (!frame) return null;
  for (const a of buildGuestAtoms(guest, dyn.atoms.length).atoms) {
    frame.positions.push(a.position.x, a.position.y, a.position.z);
    frame.velocities.push(0, 0, 0);
    frame.restraints?.push(0);
    frame.fixed?.push(false);
    frame.removed?.push(false);
  }
  return frame;
};

/**
 * The live frame as it will be once the guest `guestId` is removed: the same
 * frame minus that guest's atoms (the atoms after it move down, as they do in
 * the rebuild). See crystalFrameAddingGuest.
 */
export const crystalFrameRemovingGuest = (
  dyn: CrystalDynamics,
  source: CrystalDynamicsSnapshotSource | null,
  guestId: string,
): CrystalDynamicsSnapshot | null => {
  dyn.edited = true;
  const frame = captureCrystalDynamicsSnapshot(dyn, source);
  if (!frame) return null;
  const keep = dyn.atoms.map((a) => a.guestId !== guestId);
  const by3 = (arr: number[]) => arr.filter((_, k) => keep[Math.floor(k / 3)]);
  frame.positions = by3(frame.positions);
  frame.velocities = by3(frame.velocities);
  if (frame.restraints) frame.restraints = frame.restraints.filter((_, i) => keep[i]);
  if (frame.fixed) frame.fixed = frame.fixed.filter((_, i) => keep[i]);
  if (frame.removed) frame.removed = frame.removed.filter((_, i) => keep[i]);
  return frame;
};

/**
 * The lab setup a snapshot is stamped with (and checked against on apply):
 * the crystal's name plus the supercell tiling. Null when there is no crystal
 * loaded or the crystal has no name (an unnamed crystal can't be told apart
 * from another, so its snapshot stays count-gated).
 */
export const crystalDynamicsSnapshotSource = (
  crystal: (Pick<CrystalState, 'name'> & Partial<CrystalState>) | null | undefined,
  supercell: SupercellSize | null | undefined,
): CrystalDynamicsSnapshotSource | null => {
  if (!crystal?.name || !supercell) return null;
  const source: CrystalDynamicsSnapshotSource = {
    crystalName: crystal.name,
    nx: supercell.nx,
    ny: supercell.ny,
    nz: supercell.nz,
  };
  if (crystal.lattice && crystal.basis) source.structure = crystalStructureKey(crystal as CrystalState);
  return source;
};

/**
 * A short fingerprint of what the lab build reads from a crystal — lattice,
 * basis, dopants, defects, alloy, element charges, vacuum gap — and of the
 * force field it resolves to (the resolved id, so picking the default field
 * explicitly does not count as a change). Two crystals with the same key build
 * the same lab, so a run saved on one may resume on the other.
 */
export const crystalStructureKey = (crystal: CrystalState): string => {
  const l = crystal.lattice;
  const r = (x: number | undefined, d: number) => (typeof x === 'number' && isFinite(x) ? x.toFixed(d) : '');
  const text = JSON.stringify([
    [l.a, l.b, l.c, l.alpha, l.beta, l.gamma].map((x) => r(x, 5)),
    crystal.basis.map((b) => [b.element, r(b.fx, 6), r(b.fy, 6), r(b.fz, 6)]),
    crystal.dopants ?? null,
    crystal.defects ?? null,
    crystal.alloy ?? null,
    crystal.elementCharges ?? null,
    crystal.vacuumAng ?? 0,
    resolveForceField(crystal).id,
  ]);
  // FNV-1a, 32 bit — a stamp, not a security hash.
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0') + ':' + text.length.toString(36);
};

/**
 * Does a snapshot belong to the given lab setup? A stamped snapshot must match
 * the crystal name and supercell exactly; an unstamped (legacy) snapshot is
 * accepted so projects saved before the stamp existed still resume — their
 * apply then relies on the atom-count gate alone, as before.
 */
export const crystalDynamicsSnapshotMatches = (
  snapshot: CrystalDynamicsSnapshot,
  expected: CrystalDynamicsSnapshotSource | null | undefined,
): boolean => {
  const src = snapshot.source;
  if (!src) return true;
  if (!expected) return false;
  return (
    src.crystalName === expected.crystalName &&
    src.nx === expected.nx &&
    src.ny === expected.ny &&
    src.nz === expected.nz &&
    // A frame saved before the fingerprint existed matches on name + supercell.
    (src.structure === undefined || expected.structure === undefined || src.structure === expected.structure)
  );
};

/**
 * Restore a saved snapshot onto a freshly-built engine so a paused
 * simulation resumes in place. Two validity gates: the snapshot's source
 * stamp (crystal name + supercell, see crystalDynamicsSnapshotMatches) must
 * match `expected`, and the atom count must match the engine — a supercell /
 * guest / crystal edit changes the count. A stale snapshot is silently
 * ignored and the engine keeps its built lattice.
 * The snapshot also becomes the engine's reset baseline (initialPosition /
 * initialVelocity) so it survives the load-time reset and Reset returns to
 * the opened frame — see the per-atom note below. Returns true when the
 * snapshot was applied.
 */
export const applyCrystalDynamicsSnapshot = (
  dyn: CrystalDynamics,
  snapshot: CrystalDynamicsSnapshot | null | undefined,
  expected?: CrystalDynamicsSnapshotSource | null,
): boolean => {
  if (!snapshot) return false;
  if (!crystalDynamicsSnapshotMatches(snapshot, expected)) return false;
  const n = dyn.atoms.length;
  if (snapshot.positions.length !== n * 3 || snapshot.velocities.length !== n * 3) return false;
  for (let i = 0; i < n; i++) {
    const at = dyn.atoms[i];
    const px = snapshot.positions[i * 3];
    const py = snapshot.positions[i * 3 + 1];
    const pz = snapshot.positions[i * 3 + 2];
    const vx = snapshot.velocities[i * 3];
    const vy = snapshot.velocities[i * 3 + 1];
    const vz = snapshot.velocities[i * 3 + 2];
    at.position.set(px, py, pz);
    at.velocity.set(vx, vy, vz);
    // The saved frame is also the RESET BASELINE — the same way the molecule
    // chamber treats loaded atom state. This is load-bearing, not cosmetic:
    // setProjectState → resetProject() fires resetSimulation on every open,
    // which runs dyn.reset() (atoms ← initialPosition, velocity ←
    // initialVelocity). Leaving the baseline at the built lattice would let
    // that reset immediately wipe the resumed coordinates and zero every
    // velocity — which blanks the kinetic-energy color map on load. (For a
    // freshly-built crystal there is no snapshot, so the baseline stays at the
    // relaxed lattice and Reset still returns there.)
    at.initialPosition?.set(px, py, pz);
    at.initialVelocity?.set(vx, vy, vz);
    // Restore manual lab-edit state (restrain / cut) when the snapshot carries
    // it. Restraints anchor to the restored position (where the atom is now),
    // matching how the context-menu Restrain action seeds them.
    if (snapshot.restraints) {
      const s = snapshot.restraints[i] ?? 0;
      at.restraint = s > 0 ? new Restraint(s, at.position.clone()) : undefined;
    }
    if (snapshot.fixed) at.fixed = snapshot.fixed[i] ?? false;
    if (snapshot.removed) at.removed = snapshot.removed[i] ?? false;
  }
  dyn.indexOfStep = snapshot.indexOfStep ?? 0;
  // Mark edited so a subsequent save re-captures this state even if the engine
  // never steps (e.g. a translate-only snapshot reloaded then saved again).
  dyn.edited = true;
  // fixed / removed change the movable set the integrator + thermostat use.
  if (snapshot.fixed || snapshot.removed) dyn.countMovables();
  for (const vs of dyn.virtualSites) vs.updatePosition();
  dyn.updateKineticEnergy();
  return true;
};

/**
 * Build the engine for `scaffold` and resume `snapshot` on it. A snapshot that
 * fits replaces every coordinate and velocity, so the force field's relaxation
 * (up to 300 full force evaluations — seconds on open for EAM copper and Ewald
 * salt, on the main thread for the viewer's mirror) is skipped for it. If the
 * snapshot is refused anyway, the engine is rebuilt WITH the relaxation, exactly
 * as for a project that has no snapshot.
 */
export const configureCrystalDynamicsResuming = (
  scaffold: CrystalScaffold,
  crystal: CrystalState,
  container: MolecularContainer,
  timeStep: number,
  snapshot: CrystalDynamicsSnapshot | null | undefined,
  expected: CrystalDynamicsSnapshotSource | null,
): CrystalDynamics => {
  const n3 = scaffold.atoms.length * 3;
  const fits =
    !!snapshot &&
    crystalDynamicsSnapshotMatches(snapshot, expected) &&
    snapshot.positions.length === n3 &&
    snapshot.velocities.length === n3;
  const dyn = configureCrystalDynamics(scaffold, crystal, container, timeStep, fits);
  if (applyCrystalDynamicsSnapshot(dyn, snapshot, expected) || !fits) return dyn;
  return configureCrystalDynamics(scaffold, crystal, container, timeStep);
};
